import { baseApi } from '@/store/api/baseApi';
import { API_URL } from '@/config/app';
import type { RootState } from '@/store';
import type {
  BulkBrandIdsPayload,
  BulkBrandStatusPayload,
  Brand,
  BrandListParams,
  CreateBrandPayload,
  PaginatedBrands,
  ReorderPayload,
  UpdateBrandPayload,
} from '@/types/brand';
import { createAccessToken } from '@/utils/agent';
import { getRtkErrorMessage } from '@/utils/apiResponse';
import { isApiResponse } from '@/types/api';

function buildBrandListQuery(params: BrandListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const brandsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrands: builder.query<PaginatedBrands, BrandListParams>({
      query: (params) => ({
        url: `/brands?${buildBrandListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildBrandListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildBrandListQuery(currentArg ?? {}) !== buildBrandListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Brands' as const, id })),
              { type: 'Brands', id: 'LIST' },
            ]
          : [{ type: 'Brands', id: 'LIST' }],
    }),
    getBrand: builder.query<Brand, string>({
      query: (id) => `/brands/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Brands', id }],
    }),
    createBrand: builder.mutation<Brand, CreateBrandPayload>({
      query: (body) => ({ url: '/brands/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Brands', id: 'LIST' }],
    }),
    updateBrand: builder.mutation<Brand, { id: string; body: UpdateBrandPayload }>({
      query: ({ id, body }) => ({ url: `/brands/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Brands', id },
        { type: 'Brands', id: 'LIST' },
      ],
    }),
    deleteBrand: builder.mutation<void, string>({
      query: (id) => ({ url: `/brands/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Brands', id: 'LIST' }],
    }),
    reorderBrands: builder.mutation<Brand[], ReorderPayload>({
      query: (body) => ({ url: '/brands/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Brands', id: 'LIST' }],
    }),
    bulkUpdateBrandStatus: builder.mutation<{ count: number }, BulkBrandStatusPayload>({
      query: (body) => ({ url: '/brands/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Brands', id: 'LIST' }],
    }),
    bulkDeleteBrands: builder.mutation<{ count: number }, BulkBrandIdsPayload>({
      query: (body) => ({ url: '/brands/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Brands', id: 'LIST' }],
    }),
    uploadBrandImage: builder.mutation<{ image: string }, FormData>({
      async queryFn(formData, api) {
        const { accessToken } = (api.getState() as RootState).auth;

        try {
          const response = await fetch(`${API_URL}/brands/upload-image`, {
            method: 'POST',
            headers: {
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              'x-access-token': createAccessToken(),
            },
            body: formData,
          });

          const json: unknown = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: getRtkErrorMessage({ data: json }) ?? 'Image upload failed.',
              },
            };
          }

          const payload = isApiResponse<{ image: string }>(json) ? json.data : (json as { image: string });
          const message = isApiResponse<{ image: string }>(json) ? json.message : undefined;

          return {
            data: payload,
            ...(message ? { meta: { message } } : {}),
          };
        } catch {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: 'Image upload failed.',
            },
          };
        }
      },
    }),
  }),
});

export const {
  useGetBrandsQuery,
  useLazyGetBrandsQuery,
  useGetBrandQuery,
  useCreateBrandMutation,
  useUpdateBrandMutation,
  useDeleteBrandMutation,
  useReorderBrandsMutation,
  useBulkUpdateBrandStatusMutation,
  useBulkDeleteBrandsMutation,
  useUploadBrandImageMutation,
} = brandsApi;
